import { useState } from "react";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { AdminPanel } from "../../components/admin/AdminPanel";
import { AdminTable } from "../../components/admin/AdminTable";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { FullPageLoader } from "../../components/ui/FullPageLoader";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useUsers } from "../../hooks/useUsers";
import { FEATURE_FLAG_KEYS, featureFlagService } from "../../services/featureFlagService";
import { titleCase } from "../../lib/format";
import type { TenantPlan } from "../../types/admin";

const PLANS: TenantPlan[] = ["starter", "pro", "studio", "enterprise"];

export function AdminFeatureFlagsPage() {
  const { data, loading, error, refresh } = useFeatureFlags();
  const { data: users } = useUsers("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<TenantPlan>("enterprise");
  const [selectedKey, setSelectedKey] = useState<string>("ai_enabled");
  const [selectedUserId, setSelectedUserId] = useState("");

  async function toggleFlag(key: string, enabled: boolean, tenantId?: string | null) {
    setActionError(null);
    try {
      await featureFlagService.setFlag(key, enabled, tenantId);
      await refresh();
    } catch (caughtError) {
      setActionError(caughtError instanceof Error ? caughtError.message : "Unable to change feature flag.");
    }
  }

  async function setPackageDefault(enabled: boolean) {
    setActionError(null);
    try {
      await featureFlagService.setPackageDefault(selectedPlan, selectedKey, enabled);
      await refresh();
    } catch (caughtError) {
      setActionError(caughtError instanceof Error ? caughtError.message : "Unable to update package flag.");
    }
  }

  async function setUserOverride(enabled: boolean) {
    if (!selectedUserId) {
      setActionError("Select a user before applying a user override.");
      return;
    }

    setActionError(null);
    try {
      await featureFlagService.setUserOverride(selectedUserId, selectedKey, enabled);
      await refresh();
    } catch (caughtError) {
      setActionError(caughtError instanceof Error ? caughtError.message : "Unable to update user override.");
    }
  }

  async function clearUserOverride(userId: string, key: string) {
    setActionError(null);
    try {
      await featureFlagService.clearUserOverride(userId, key);
      await refresh();
    } catch (caughtError) {
      setActionError(caughtError instanceof Error ? caughtError.message : "Unable to clear user override.");
    }
  }

  if (loading) return <FullPageLoader label="Loading feature flags" />;
  if (error) return <EmptyState title="Unable to load feature flags.">{error}</EmptyState>;

  const flags = data?.flags ?? [];
  const packageDefaults = data?.packageDefaults ?? [];
  const userOverrides = data?.userOverrides ?? [];
  const globalFlags = flags.filter((flag) => !flag.tenant_id);
  const missingGlobalFlags = FEATURE_FLAG_KEYS.filter((key) => !globalFlags.some((flag) => flag.key === key));

  return (
    <div>
      <AdminPageHeader title="Feature Flags" description="Control global, package and user-level rollout switches without building the flagged features yet." />
      {actionError ? <div className="mb-5 rounded-md border border-red-900/20 bg-red-50 p-4 text-sm text-red-900">{actionError}</div> : null}
      <div className="grid gap-6 xl:grid-cols-[1fr_420px]">
        <AdminPanel title="Global and tenant flags">
          <AdminTable headers={["Key", "Scope", "State", "Actions"]}>
            {flags.map((flag) => (
              <tr key={flag.id}>
                <td className="px-4 py-3 font-medium text-ink">{flag.key}</td>
                <td className="px-4 py-3">{flag.tenants?.name ?? "Global"}</td>
                <td className="px-4 py-3"><Badge tone={flag.enabled ? "success" : "neutral"}>{flag.enabled ? "enabled" : "disabled"}</Badge></td>
                <td className="px-4 py-3">
                  <Button variant="secondary" onClick={() => void toggleFlag(flag.key, !flag.enabled, flag.tenant_id)}>
                    {flag.enabled ? "Disable" : "Enable"}
                  </Button>
                </td>
              </tr>
            ))}
          </AdminTable>
        </AdminPanel>
        <div className="space-y-6">
        <AdminPanel title="Package defaults">
          <div className="space-y-4">
            <label className="block text-sm font-medium text-ink">
              Package
              <select className="mt-2 block w-full rounded-md border border-line bg-field px-3 py-2" value={selectedPlan} onChange={(event) => setSelectedPlan(event.target.value as TenantPlan)}>
                {PLANS.map((plan) => <option key={plan} value={plan}>{plan}</option>)}
              </select>
            </label>
            <label className="block text-sm font-medium text-ink">
              Flag
              <select className="mt-2 block w-full rounded-md border border-line bg-field px-3 py-2" value={selectedKey} onChange={(event) => setSelectedKey(event.target.value)}>
                {FEATURE_FLAG_KEYS.map((key) => <option key={key} value={key}>{titleCase(key)}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="secondary" onClick={() => void setPackageDefault(true)}>Enable for package</Button>
              <Button variant="secondary" onClick={() => void setPackageDefault(false)}>Disable for package</Button>
            </div>
            <div className="max-h-72 overflow-y-auto rounded-md border border-line">
              {packageDefaults.filter((flag) => flag.plan === selectedPlan).map((flag) => (
                <div className="flex items-center justify-between border-b border-line px-3 py-2 text-sm last:border-b-0" key={flag.id}>
                  <span>{titleCase(flag.key)}</span>
                  <Badge tone={flag.enabled ? "success" : "neutral"}>{flag.enabled ? "on" : "off"}</Badge>
                </div>
              ))}
            </div>
          </div>
          {missingGlobalFlags.length ? (
            <p className="mt-4 text-sm text-brass">
              {missingGlobalFlags.length} default flags will appear after migration seed or first update.
            </p>
          ) : null}
        </AdminPanel>
        <AdminPanel title="User overrides">
          <div className="space-y-4">
            <label className="block text-sm font-medium text-ink">
              User
              <select className="mt-2 block w-full rounded-md border border-line bg-field px-3 py-2" value={selectedUserId} onChange={(event) => setSelectedUserId(event.target.value)}>
                <option value="">Select user</option>
                {users?.map((user) => <option key={user.id} value={user.id}>{user.full_name || user.email}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="secondary" onClick={() => void setUserOverride(true)}>Enable for user</Button>
              <Button variant="secondary" onClick={() => void setUserOverride(false)}>Disable for user</Button>
            </div>
            <div className="space-y-2">
              {userOverrides.length ? userOverrides.map((override) => (
                <div className="rounded-md border border-line bg-field p-3 text-sm" key={override.id}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-ink">{override.profiles?.full_name || override.profiles?.email}</p>
                      <p className="mt-1 text-graphite/65">{titleCase(override.key)}</p>
                    </div>
                    <Badge tone={override.enabled ? "success" : "neutral"}>{override.enabled ? "on" : "off"}</Badge>
                  </div>
                  <Button className="mt-3" variant="secondary" onClick={() => void clearUserOverride(override.user_id, override.key)}>Clear override</Button>
                </div>
              )) : <p className="text-sm text-graphite/65">No user overrides yet.</p>}
            </div>
          </div>
        </AdminPanel>
        </div>
      </div>
    </div>
  );
}
